import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';

interface SearchableSelectProps {
  options: Array<{ id: string; label: string }>;
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
}

const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const INITIAL_RESULTS = 5;
const MAX_FILTERED_RESULTS = 8;

interface DropdownPos {
  top: number;
  left: number;
  width: number;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Buscar…',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((o) => o.id === value)?.label ?? '';

  // Sincronizar el texto mostrado cuando cambia el valor seleccionado
  useEffect(() => {
    setInputText(selectedLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const openDropdown = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setInputText(selectedLabel);
  };

  // Cerrar al hacer clic fuera o al hacer scroll/redimensionar (la lista es de posición fija)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // El dropdown está en un portal: validar también su contenido
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        closeDropdown();
      }
    };
    const closeOnly = () => setIsOpen(false);
    document.addEventListener('mousedown', handler);
    document.addEventListener('scroll', closeOnly, true);
    window.addEventListener('resize', closeOnly);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('scroll', closeOnly, true);
      window.removeEventListener('resize', closeOnly);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedLabel]);

  const isFiltering = inputText.trim() !== '' && inputText.trim() !== selectedLabel;
  const filtered = isFiltering
    ? options
        .filter((o) => normalize(o.label).includes(normalize(inputText.trim())))
        .slice(0, MAX_FILTERED_RESULTS)
    : options.slice(0, INITIAL_RESULTS);

  const handleSelect = (id: string, label: string) => {
    onChange(id);
    setInputText(label);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setInputText('');
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          placeholder={placeholder}
          onFocus={openDropdown}
          onChange={(e) => {
            setInputText(e.target.value);
            openDropdown();
          }}
          className="w-full px-2.5 py-2 pr-7 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 outline-none bg-white"
        />
        {value ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            title="Quitar selección"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        )}
      </div>

      {isOpen &&
        dropdownPos &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
            }}
            className="z-[100] max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl"
          >
            {isFiltering && filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400">Sin resultados</p>
            ) : (
              <>
                {filtered.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => handleSelect(o.id, o.label)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-purple-50 transition-colors ${
                      o.id === value
                        ? 'bg-purple-50 text-purple-700 font-medium'
                        : 'text-gray-700'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
                {!isFiltering && options.length > INITIAL_RESULTS && (
                  <p className="px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100">
                    Escribe para buscar entre {options.length} opciones
                  </p>
                )}
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};
